// ABOUTME: Home page component with welcome, metrics, and chat interface.
// ABOUTME: Shows a focused "get started" experience when no data source is connected.

import { useState, useRef, useEffect, useCallback, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  ArrowUp,
  Loader2,
  Database,
  Sparkles,
  TrendingUp,
  BarChart3,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useApp } from '@/context/AppContext';
import { Card } from '@/components/ui/Card';
import { Tooltip } from '@/components/ui/Tooltip';
import { investigationApi, workspaceMetricsApi } from '@/services/api/client';
import { SpecializedContentButton } from '@/components/investigation';
import type { WorkspaceMetrics } from '@/types/api';
import { isDataSourcesEnabled } from '@/config/features';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

// Default suggested prompts for home page (fallback)
const defaultPrompts = [
  'What are the top 10 customers by total order value?',
  'Show me the monthly revenue trend for 1997',
  'Which product categories generate the most revenue?',
];

export function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startNewSession, availableWorkspaces, currentWorkspaceId, userProfileName } = useApp();

  // Use backend profile name if available, fall back to auth context
  const displayName = userProfileName?.split(' ')[0] || user?.name?.split(' ')[0] || 'there';
  const [inputValue, setInputValue] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>(defaultPrompts);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const promptsLoadedRef = useRef(false);
  const [metrics, setMetrics] = useState<WorkspaceMetrics | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);
  const dataSourcesEnabled = isDataSourcesEnabled();

  // Fetch AI-generated suggested prompts for the workspace
  const loadSuggestedPrompts = useCallback(async () => {
    if (promptsLoadedRef.current) return;

    setIsLoadingPrompts(true);
    try {
      const response = await investigationApi.getWorkspaceSuggestedPrompts();
      if (response.prompts && response.prompts.length > 0) {
        setSuggestedPrompts(response.prompts);
      }
      promptsLoadedRef.current = true;
    } catch (err) {
      console.debug('Failed to load workspace suggested prompts:', err);
      // Keep default prompts on error
    } finally {
      setIsLoadingPrompts(false);
    }
  }, []);

  // Fetch workspace metrics
  const loadMetrics = useCallback(async () => {
    setIsLoadingMetrics(true);
    try {
      const data = await workspaceMetricsApi.get();
      setMetrics(data);
    } catch (err) {
      console.error('Failed to load workspace metrics:', err);
      // Set zeros on error
      setMetrics({
        data_sources: 0,
        tables: 0,
        cells: 0,
        calculated_at: new Date().toISOString(),
      });
    } finally {
      setIsLoadingMetrics(false);
    }
  }, []);

  // Load prompts and metrics on mount
  useEffect(() => {
    loadSuggestedPrompts();
    loadMetrics();
  }, [loadSuggestedPrompts, loadMetrics]);

  // Poll metrics while schema crawl is in progress (tables still 0)
  useEffect(() => {
    if (!metrics || metrics.data_sources === 0 || metrics.tables > 0) return;

    const interval = setInterval(async () => {
      try {
        const data = await workspaceMetricsApi.get();
        setMetrics(data);
        if (data.tables > 0) {
          clearInterval(interval);
        }
      } catch {
        // Ignore polling errors
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [metrics]);

  // Get current workspace name
  const currentWorkspace = availableWorkspaces.find(w => w.workspace_id === currentWorkspaceId);
  const workspaceName = currentWorkspace?.workspace_name || 'Your Workspace';

  // Format large numbers with compact notation (K, M, B, T)
  const formatNumber = (num: number) => {
    if (num < 1000) {
      return num.toString();
    }
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    }).format(num);
  };

  // Schema crawl may still be running if we have data sources but no tables yet
  const isSchemaCrawling = metrics !== null && metrics.data_sources > 0 && metrics.tables === 0;

  // Metric cards data
  const metricCards = [
    {
      id: 'data-sources',
      title: 'Data Sources',
      value: metrics ? formatNumber(metrics.data_sources) : '—',
      calculating: false,
      tooltip: undefined,
      onClick: () => navigate('/data-sources'),
    },
    {
      id: 'tables',
      title: 'Tables',
      value: metrics ? formatNumber(metrics.tables) : '—',
      calculating: isSchemaCrawling,
      tooltip: undefined,
    },
    {
      id: 'cells',
      title: 'Cells',
      value: metrics ? formatNumber(metrics.cells) : '—',
      calculating: isSchemaCrawling,
      tooltip: 'Total data points across all tables (rows × columns). Represents the complete size of your workspace data.',
    },
  ];

  // Handle starting a new investigation with a message
  const handleStartInvestigation = async (message: string) => {
    if (!message.trim() || isCreating) return;

    setIsCreating(true);
    try {
      // startNewSession verifies the investigation is readable before returning
      const newId = await startNewSession();
      // Navigate to the investigation with the initial message as a query param
      navigate(`/investigations/${newId}?q=${encodeURIComponent(message.trim())}`);
    } catch (err) {
      console.error('Failed to start investigation:', err);
      setIsCreating(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    handleStartInvestigation(inputValue);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleStartInvestigation(suggestion);
  };

  // Check if user has connected a data source
  const hasDataSource = metrics !== null && metrics.data_sources > 0;

  // Show loading state while checking metrics
  if (isLoadingMetrics) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-surface-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Empty state: No data source connected yet
  if (!hasDataSource) {
    const features = [
      {
        icon: <Sparkles className="w-5 h-5" />,
        title: 'Ask questions in plain English',
        description: 'No SQL required. Just describe what you want to know.',
      },
      {
        icon: <TrendingUp className="w-5 h-5" />,
        title: 'Discover trends and patterns',
        description: 'AI finds insights you might have missed.',
      },
      {
        icon: <BarChart3 className="w-5 h-5" />,
        title: 'Create reports instantly',
        description: 'Turn your findings into shareable dashboards.',
      },
    ];

    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-16">
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="space-y-10"
          >
            {/* Welcome Header */}
            <motion.div variants={item} className="text-center space-y-3">
              <h1 className="text-4xl font-bold tracking-tight text-surface-900">
                Welcome, {displayName}
              </h1>
              <p className="text-lg max-w-md mx-auto text-surface-600">
                Configure a data source to unlock <span className="text-primary-600 font-medium">AI-powered insights</span> about your data.
              </p>
            </motion.div>

            {/* Feature highlights */}
            <motion.div variants={item} className="space-y-3">
              {features.map((feature) => (
                <div
                  key={feature.title}
                  className="flex items-start gap-4 p-4 rounded-sm bg-surface-0 border border-surface-200"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-surface-900">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-surface-600">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* CTA Section */}
            <motion.div variants={item} className="text-center">
              <div className="p-8 rounded-sm bg-surface-0 border border-surface-200">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-500 flex items-center justify-center">
                  <Database className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-surface-900">
                  Ready to get started?
                </h2>
                <p className="mb-6 max-w-sm mx-auto text-surface-600">
                  Contact your administrator to configure a data source for this workspace.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Normal state: Has data source connected
  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="space-y-12"
        >
          {/* Welcome Header */}
          <motion.div variants={item} className="text-center space-y-3">
            <h1 className="text-5xl font-semibold text-surface-900 tracking-tight">
              Welcome, {displayName}
            </h1>
            <p className="text-surface-500 text-lg">
              Working in <span className="text-surface-700 font-medium">{workspaceName}</span>
            </p>
          </motion.div>

          {dataSourcesEnabled && (
            <motion.div variants={item}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {metricCards.map((metric) => {
                const cardContent = (
                  <>
                    <div className="mb-2">
                      <span className="label">
                        {metric.title}
                      </span>
                    </div>
                    {metric.calculating ? (
                      <div className="text-sm font-medium text-surface-400 animate-pulse">
                        Calculating...
                      </div>
                    ) : (
                      <div className="text-3xl font-mono font-semibold text-surface-900">
                        {metric.value}
                      </div>
                    )}
                  </>
                );

                const card = metric.onClick ? (
                  <Card
                    key={metric.id}
                    variant="glass"
                    className="card-stat cursor-pointer hover:border-surface-300 transition-colors"
                    onClick={metric.onClick}
                  >
                    {cardContent}
                  </Card>
                ) : (
                  <Card key={metric.id} variant="glass" className="card-stat">
                    {cardContent}
                  </Card>
                );

                return metric.tooltip ? (
                  <Tooltip key={metric.id} content={metric.tooltip}>
                    {card}
                  </Tooltip>
                ) : (
                  card
                );
                })}
              </div>
            </motion.div>
          )}

          {/* Quick Actions */}
          <motion.div variants={item}>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => navigate('/investigations')}
                className="btn btn-secondary btn-md"
              >
                <MessageSquare className="w-4 h-4" />
                <span>View Investigations</span>
              </button>
            </div>
          </motion.div>

          {/* How can I help you section */}
          <motion.div variants={item} className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-medium text-surface-800 mb-2">
                How can I help you today?
              </h2>
              <p className="text-surface-500">
                Ask me a question about your data
              </p>
            </div>

            {/* Suggested Prompts */}
            <div className="flex flex-wrap justify-center gap-3 min-h-[44px]">
              {isLoadingPrompts ? (
                <div className="flex items-center gap-2 text-surface-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Loading suggestions...</span>
                </div>
              ) : (
                suggestedPrompts.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={isCreating}
                    className="badge badge-default px-4 py-2 text-sm hover:bg-surface-200 hover:text-surface-700 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {suggestion}
                  </button>
                ))
              )}
            </div>

            {/* Chat Input */}
            <div className="max-w-2xl mx-auto">
              <form onSubmit={handleSubmit} className="relative">
                <div className="flex items-center bg-surface-0 border border-surface-200 rounded-sm shadow-md">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={isCreating ? 'Starting investigation...' : 'Ask a question...'}
                    disabled={isCreating}
                    className="flex-1 bg-transparent py-4 pl-5 pr-24 text-base text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-0 focus:border-transparent border-none disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ outline: 'none', border: 'none', boxShadow: 'none' }}
                  />

                  <SpecializedContentButton className="absolute right-14 top-1/2 -translate-y-1/2 p-2.5" />

                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isCreating}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-primary-500 hover:bg-primary-600 rounded-sm text-white shadow-primary cursor-pointer transition-all hover:-translate-y-[calc(50%+2px)] disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-[-50%] disabled:shadow-none"
                  >
                    {isCreating ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <ArrowUp className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
